import React, { useEffect, useRef, useState } from "react";
import SimpleBar from "simplebar-react";
import { MoreVertical, Star, Send, Mic, Image } from "lucide-react";
import avatar1 from "assets/images/users/avatar-1.png";
import avatar5 from "assets/images/users/avatar-5.png";
import userDummyImage from "assets/images/users/user-dummy-img.jpg";
import ChatFooter from "./components/ChatFooter";
import { useSocket } from "context/socket";
import { useAppServices } from "hook/services";
import { useParams } from "react-router-dom";
import { Contact } from "pages/Location/Contacts/components/DataTypes";

interface Message {
  id: number;
  msg: string;
  isSender: boolean;
  bookmark?: boolean;
  attachments?: { img: string }[];
}

const Chat = ({ contact }: { contact: Contact }) => {
  const socket = useSocket();
  const AppService = useAppServices();
  const { contactId } = useParams();
  const [chatUser] = useState({
    name: "Marie Prohaska",
    image: avatar5,
    designation: "Angular Developer",
  });

  const [messages, setMessages] = useState<Message[]>([]);

  const [activeCoversation, setActiveCoversation] = useState(null);
  const chatRef = useRef<any>(null);
  const getConversation = async () => {
    const { response } = await AppService.conversation.get({
      query: `contactId=${contactId}`,
    });
    if (response) {
      setActiveCoversation(response.data);
    }
  };
  const getMessages = async () => {
    const { response } = await AppService.message.filter({
      query: `contactId=${contactId}`,
    });
    if (response) {
      setMessages(response.data);
    }
  };
  let pageLoad = true;
  const onload = () => {
    if (pageLoad) {
      getConversation();
      getMessages();
      pageLoad = false;
    }
  };

  useEffect(() => {
    onload();
  }, []);

  useEffect(() => {
    if (chatRef.current?.el) {
      const scrollElement = chatRef.current.getScrollElement();
      scrollElement.scrollTop = scrollElement.scrollHeight;
    }
  }, [messages]);

  const handleBookmark = (id: number) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === id ? { ...msg, bookmark: !msg.bookmark } : msg
      )
    );
  };

  const handleDelete = (id: number) => {
    setMessages((prev) => prev.filter((msg) => msg.id !== id));
  };

  const handleCopy = (msg: string) => {
    navigator.clipboard.writeText(msg);
  };

  useEffect(() => {
    if (!socket) return;

    const handleMessageResponse = (data: any) => {
      setMessages((prev) => [...prev, data]);
    };
    const handleConversationCreated = (data: any) => {
      console.log(data, "handleConversationCreated");
      setActiveCoversation(data);
    };
    socket.on("messageResponse", handleMessageResponse);
    socket.on("coversationCreated", handleConversationCreated);

    return () => {
      socket.off("messageResponse", handleMessageResponse);
      socket.off("messageResponse", handleConversationCreated);
    };
  }, [socket]);
  return (
    <div className="container-fluid group-data-[content=boxed]:max-w-boxed mx-auto relative">
      <div className="card max-h-[calh-[calc(100vh_-_theme('spacing.10')_*_6)] xl:min-h-[calc(100vh_-_theme('height.header')_*_2.4)] bg-white dark:bg-zinc-800  flex flex-col">
        {/* Chat Header */}
        <div className="border-b border-slate-200 dark:border-zinc-700 p-4 flex items-center gap-3">
          <img
            src={chatUser.image || userDummyImage}
            alt="user"
            className="size-10 rounded-full object-cover"
          />
          <div>
            <h6 className="font-semibold text-gray-900 dark:text-zinc-100">
              {chatUser.name}
            </h6>
            <p className="text-sm text-gray-500 dark:text-zinc-400">
              {chatUser.designation}
            </p>
          </div>
        </div>
        {/* Chat Messages */}
        <div className="flex-1 bg-slate-50 dark:bg-zinc-700/40">
          <SimpleBar
            ref={chatRef}
            className="p-5 h-[calc(100vh_-_510px)] xl:h-[calc(100vh_-_480px)]"
          >
            <ul className="flex flex-col gap-5">
              {messages.map((msg: any) => (
                <li
                  key={msg._id}
                  className={`flex ${
                    msg.direction == "outbound"
                      ? "justify-end"
                      : "justify-start"
                  }`}
                >
                  <div
                    className={`flex gap-3 items-end max-w-[70%] ${
                      msg.direction == "outbound" ? "flex-row-reverse" : ""
                    }`}
                  >
                    <img
                      src={
                        msg.direction == "outbound" ? avatar1 : chatUser.image
                      }
                      alt=""
                      className="size-8 rounded-full object-cover"
                    />
                    <div
                      className={`relative p-3 rounded-2xl shadow-sm ${
                        msg.direction == "outbound"
                          ? "bg-blue-500 text-white rounded-br-none"
                          : "bg-white dark:bg-zinc-600 rounded-bl-none"
                      }`}
                    >
                      <p className="text-sm leading-relaxed">{msg.message}</p>

                      {/* Dropdown Menu */}
                      <div className="absolute top-1 right-1 opacity-0 hover:opacity-100 transition">
                        <button className="p-1 rounded-full bg-transparent hover:bg-gray-200 dark:hover:bg-zinc-500">
                          <MoreVertical className="size-4 text-gray-600 dark:text-zinc-200" />
                        </button>
                        <div className="absolute right-0 mt-1 hidden group-hover:block bg-white dark:bg-zinc-600 rounded-md shadow-md p-2 text-sm">
                          <button
                            onClick={() => handleCopy(msg.msg)}
                            className="block px-2 py-1 hover:bg-gray-100 dark:hover:bg-zinc-500 w-full text-left"
                          >
                            Copy
                          </button>
                          <button
                            onClick={() => handleBookmark(msg.id)}
                            className="block px-2 py-1 hover:bg-gray-100 dark:hover:bg-zinc-500 w-full text-left"
                          >
                            {msg.bookmark ? "Unbookmark" : "Bookmark"}
                          </button>
                          <button
                            onClick={() => handleDelete(msg.id)}
                            className="block px-2 py-1 text-red-500 hover:bg-gray-100 dark:hover:bg-zinc-500 w-full text-left"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </SimpleBar>
        </div>
        <ChatFooter activeCoversation={activeCoversation} contact={contact} />
      </div>
    </div>
  );
};

export default Chat;
